import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Search, FileText, Target, ListChecks, Award, GraduationCap } from "lucide-react";
import heroPhoto from "@/assets/home-hero.jpg";
import PageHead from "@/components/PageHead";
import AppShell from "@/components/shell/AppShell";
import { fetchAttempts, type Attempt } from "@/lib/assessmentAttempts";
import { useCareerDna } from "@/hooks/useCareerDna";
import { useAssessmentEntry } from "@/hooks/useAssessmentEntry";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useDisplayName } from "@/hooks/useDisplayName";
import { supabase } from "@/integrations/supabase/client";
import { fetchMyXp, levelFor, type XpSummary } from "@/lib/xp";
import { resolveAvatarUrl } from "@/lib/avatar";
import { fetchMyJourney, buildSchedule, deriveBadges } from "@/lib/journey";
import { isPreviewMode } from "@/lib/previewMode";
import { OfferCrumb } from "@/components/OfferCrumbs";
import { LearnerQuest, type QuestStage } from "@/components/journey/LearnerQuest";
import CareerSwitcherRoadmap from "@/components/career/CareerSwitcherRoadmap";

import { localSavedCount, fetchSavedCareers } from "@/lib/savedCareers";

const TILES = [
  { to: "/careers", label: "Explore careers", hint: "5,000+ roles, sorted for you", icon: Compass },
  { to: "/report", label: "Your report", hint: "Strengths and why careers matched", icon: FileText },
  { to: "/colleges", label: "College Match", hint: "Courses and application roadmap", icon: GraduationCap },
  { to: "/next-steps", label: "Next steps", hint: "Small actions for this month", icon: ListChecks },
  { to: "/vision-board", label: "Vision Board", hint: "Careers you have pinned", icon: Target },
  { to: "/certifications", label: "Certifications", hint: "Courses matched to your careers", icon: Award },
];

type Status = "in_progress" | "completed" | "scored" | string;

/** "3 days ago", for the last-activity line. Kept human, never a timestamp. */
function lastActivity(rows: Attempt[]): string | null {
  const stamps = rows
    .map((r) => r.started_at ?? r.created_at)
    .filter((s): s is string => Boolean(s))
    .sort()
    .reverse();
  if (!stamps.length) return null;
  const days = Math.floor((Date.now() - new Date(stamps[0]).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

/** Progress ring — one number, no chart library. */
function Ring({ pct, label, value }: { pct: number; label: string; value: string }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-[116px] w-[116px] shrink-0">
      <svg viewBox="0 0 108 108" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="54" cy="54" r={r} fill="none" strokeWidth="8" className="stroke-muted" />
        <circle
          cx="54"
          cy="54"
          r={r}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          className="stroke-primary transition-[stroke-dashoffset] duration-700 ease-out"
          strokeDasharray={c}
          strokeDashoffset={c - (Math.min(100, Math.max(0, pct)) / 100) * c}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-bold leading-none">{value}</span>
        <span className="mt-1 text-[11px] text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

export default function HomeV2() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const { name, refresh: setSharedName } = useDisplayName();
  const [avatar, setAvatar] = useState<string | null>(null);
  const [xp, setXp] = useState<XpSummary | null>(null);
  const [assessments, setAssessments] = useState<Attempt[]>([]);
  const [savedCount, setSavedCount] = useState(0);
  const [badgesEarned, setBadgesEarned] = useState<{ earned: number; total: number }>({
    earned: 0,
    total: 0,
  });
  const [journeyPct, setJourneyPct] = useState(0);
  const [hasPlan, setHasPlan] = useState(false);
  const { data: dna } = useCareerDna();
  const entry = useAssessmentEntry();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!user) {
      // Dev-only preview door: show the shell rather than an endless skeleton.
      if (isPreviewMode()) setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      // Each read is settled on its own: one failing call (a missing profile
      // row, a journey RPC hiccup) must never blank the whole dashboard.
      const [attemptsR, profileR, xpR, journeyR] = await Promise.allSettled([
        // Both records of "a test was taken", merged — a blocked `assessments`
        // insert can never hide a run that the runtime saved.
        fetchAttempts(user.id),
        supabase.from("profiles").select("full_name, email, avatar_url").eq("id", user.id).maybeSingle(),
        fetchMyXp(user.id),
        fetchMyJourney(),
      ]);
      if (!active) return;
      const attempts = attemptsR.status === "fulfilled" ? attemptsR.value : [];
      const pData = profileR.status === "fulfilled" ? profileR.value.data : null;
      const xpData = xpR.status === "fulfilled" ? xpR.value : null;
      const journey =
        journeyR.status === "fulfilled"
          ? journeyR.value
          : ({ membership: null, milestones: [] } as Awaited<ReturnType<typeof fetchMyJourney>>);
      for (const r of [attemptsR, profileR, xpR, journeyR]) {
        // eslint-disable-next-line no-console
        if (r.status === "rejected") console.error("[wcc360.home] read failed:", r.reason);
      }
      setAssessments(attempts);
      setSharedName(pData?.full_name || pData?.email?.split("@")[0] || user.email?.split("@")[0] || null);
      void resolveAvatarUrl(pData?.avatar_url ?? null).then((u) => active && setAvatar(u));
      setXp(xpData);
      void supabase.from("next_step_plans").select("id", { count: "exact", head: true }).eq("user_id", user.id)
        .then(({ count }) => active && setHasPlan((count ?? 0) > 0));
      // Show the cached count immediately, then reconcile against
      // `saved_careers` so the card always agrees with the database.
      setSavedCount(localSavedCount());
      void fetchSavedCareers(user.id).then((rows) => {
        if (active) setSavedCount(rows.length);
      });
      const steps = buildSchedule(journey.membership, journey.milestones);
      const badges = deriveBadges(steps);
      setBadgesEarned({ earned: badges.filter((b) => b.earned).length, total: badges.length });
      const done = steps.filter((s) => s.status === "completed").length;
      setJourneyPct(steps.length ? Math.round((done / steps.length) * 100) : 0);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const level = useMemo(() => (xp ? levelFor(xp.totalXp) : null), [xp]);
  const statuses = useMemo<Status[]>(() => assessments.map((a) => a.status), [assessments]);
  const completedAttempt = assessments.find((a) => a.status === "scored" || a.status === "completed");
  // A finished run counts, whether or not the sealed report has landed. The
  // Career DNA™ context is the second signal: if the shell can read a result,
  // the front door must offer it rather than asking for the walk again.
  const hasResult = Boolean(completedAttempt) || Boolean(dna?.archetypePrimary);
  const activity = lastActivity(assessments);
  const topMatches = (dna?.matches ?? []).slice(0, 3);
  const stages: QuestStage[] = [
    { id: "discover", title: "Take your discovery", blurb: "Short, playful choices — no right answers.", to: entry.to, xp: 100, done: hasResult },
    { id: "mirror", title: "See your mirror moment", blurb: "A first look at who you are today.", to: "/mirror", xp: 25, done: hasResult },
    { id: "report", title: "Read your living report", blurb: "Strengths, story and why careers matched.", to: "/report", xp: 50, done: hasResult && Boolean(dna?.archetypePrimary) },
    { id: "explore", title: "Save careers you love", blurb: "Pin roles to your vision board.", to: "/careers", xp: 50, done: savedCount > 0 },
    { id: "next", title: "Unlock your next steps", blurb: "Five small actions for this month.", to: "/next-steps", xp: 75, done: hasPlan },
  ];

  // Exactly one primary action, always — resolved by the shared front-door
  // hook so this page, the Explorer cards and the shell can never disagree.
  const primary = {
    label: entry.state === "none" ? "Begin your discovery" : entry.label,
    to: entry.to,
    helper: entry.helper,
  };



  const initials = name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <AppShell title="Home">
      <PageHead
        title="Home | WeKIT Career Clarity 360™"
        description="Your discovery journey at a glance — progress, XP and the one thing to do next."
        path="/home"
      />

      <div className="mb-4 flex">
        <OfferCrumb showGlobal />
      </div>


      {loading ? (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-16 w-full rounded-3xl" />
          <Skeleton className="h-44 w-full rounded-3xl" />
          <Skeleton className="h-14 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-3xl" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* IBM SkillsBuild-style hero: question, search, stat circles, accent CTA tile */}
          <section className="grid gap-0 overflow-hidden rounded-3xl border border-border/50 md:grid-cols-[1.4fr_1fr]" aria-labelledby="hero-q">
            <div className="bg-card p-6 sm:p-8">
              <p className="text-sm text-muted-foreground">
                Welcome, {name}{level ? ` · ${level.current.name}` : ""}{activity ? ` · last here ${activity}` : ""}
              </p>
              <h1 id="hero-q" className="font-display mt-2 text-3xl font-light tracking-tight sm:text-4xl">
                What will you discover today?
              </h1>
              <form
                role="search"
                className="relative mt-5 max-w-md"
                onSubmit={(e) => { e.preventDefault(); navigate(`/careers${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ""}`); }}
              >
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <label htmlFor="home-search" className="sr-only">Search careers</label>
                <input
                  id="home-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search 5,000+ careers"
                  className="h-12 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
              </form>
              <dl className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
                {[
                  { v: assessments.length, l: "Discoveries completed" },
                  { v: badgesEarned.earned, l: "Badges earned" },
                  { v: xp?.currentStreak ?? 0, l: "Day streak" },
                ].map((st) => (
                  <div key={st.l} className="flex items-center gap-2.5">
                    <dd className="grid h-11 w-11 place-items-center rounded-full bg-primary font-display text-base font-semibold text-primary-foreground">{st.v}</dd>
                    <dt className="text-sm font-medium">{st.l}</dt>
                  </div>
                ))}
              </dl>
            </div>
            <div className="relative min-h-[260px]">
            <img src={heroPhoto} alt="Two young professionals planning their careers together at a laptop" width={1200} height={800} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-card via-card/30 to-transparent max-md:hidden" aria-hidden="true" />
            <Link
              to={primary.to}
              className="group absolute bottom-4 right-4 flex w-[min(20rem,calc(100%-2rem))] flex-col justify-between gap-4 bg-primary p-5 shadow-lg text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring "
            >
              <span>
                <span className="text-xs font-semibold uppercase tracking-[0.14em] opacity-80">Your next step</span>
                <span className="mt-2 block font-display text-lg font-semibold leading-snug">{primary.helper}</span>
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-semibold">
                {primary.label}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </Link>
            </div>
          </section>

          <CareerSwitcherRoadmap compact />

          {/* Quick navigation tiles — one tap to the main areas */}
          <section aria-labelledby="jump-heading">
            <h2 id="jump-heading" className="mb-3 font-display text-xl font-light tracking-tight">Make the most of WeKIT</h2>
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {TILES.map((t) => (
                <li key={t.to}>
                  <Link
                    to={t.to}
                    className="group flex h-full flex-col gap-3 rounded-2xl border border-border/50 bg-card p-4 transition-colors hover:border-primary/50 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/12 text-primary">
                      <t.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-[15px] font-semibold">{t.label}</span>
                      <span className="mt-0.5 block text-sm leading-5 text-muted-foreground">{t.hint}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <LearnerQuest stages={stages} />

          {/* 4. Careers that fit you — straight from Career DNA™, no re-ranking */}
          <section aria-labelledby="fit-heading">
            <div className="mb-3 flex items-center justify-between px-1">
              <h2 id="fit-heading" className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Careers that fit you
              </h2>
              {topMatches.length > 0 && (
                <Link to="/hub/dna" className="inline-flex min-h-11 items-center text-sm font-medium text-primary hover:underline">
                  See all
                </Link>
              )}
            </div>
            {topMatches.length === 0 ? (
              <div className="calm-card p-6 text-sm text-muted-foreground">
                Your matches appear here once you finish your discovery. Until then, explore freely.
              </div>
            ) : (
              <ul className="calm-card overflow-hidden divide-y divide-border/40">
                {topMatches.map((m, i) => (
                  <li key={m.id}>
                    <Link
                      to={`/careers/${m.id}`}
                      className="lift flex min-h-[68px] items-center gap-4 px-5 py-3 transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    >
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/12 font-display text-sm font-semibold text-primary">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-medium">{m.name}</span>
                        <span className="block truncate text-sm text-muted-foreground">
                          {m.explanation || m.cluster}
                        </span>
                      </span>
                      {m.tier && (
                        <span className="shrink-0 rounded-full bg-primary/12 px-2.5 py-1 text-xs font-semibold text-primary">
                          {m.tier}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 5. Status — collapsed by default */}
          {level && (
            <details className="calm-card-quiet group rounded-3xl p-5">
              <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm">
                <span className="font-medium">
                  {xp?.totalXp ?? 0} XP · {level.next ? `${level.next.min - (xp?.totalXp ?? 0)} to ${level.next.name}` : "Top level"}
                </span>
                <span className="text-muted-foreground group-open:hidden">Show progress</span>
                <span className="hidden text-muted-foreground group-open:inline">Hide</span>
              </summary>
              <div
                className="mt-3 h-2.5 overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-valuenow={level.progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Progress to ${level.next?.name ?? "top level"}`}
              >
                <div className="h-full rounded-full bg-primary" style={{ width: `${level.progressPct}%` }} />
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div><dt className="text-muted-foreground">Journey</dt><dd className="font-semibold">{journeyPct}%</dd></div>
                <div><dt className="text-muted-foreground">Best streak</dt><dd className="font-semibold">{xp?.longestStreak ?? 0} days</dd></div>
                <div><dt className="text-muted-foreground">Discoveries</dt><dd className="font-semibold">{assessments.length}</dd></div>
                <div><dt className="text-muted-foreground">Saved careers</dt><dd className="font-semibold">{savedCount}</dd></div>
                <div><dt className="text-muted-foreground">Badges</dt><dd className="font-semibold">{badgesEarned.earned} of {badgesEarned.total}</dd></div>
              </dl>
            </details>
          )}
        </div>
      )}
    </AppShell>
  );
}
